/**
 * Turning a hash into a decision, as a pure function.
 *
 * WHY THE ROUTING IS A FUNCTION AND NOT A SWITCH IN THE COMPONENT
 * --------------------------------------------------------------
 * The `#/documents/new` collision is the reason. `^#/documents/(.+)$` matches
 * `#/documents/new`, so whichever branch is checked first silently decides
 * whether "new" opens the editor or reports a missing document. As a component
 * that ordering is invisible; as a function it is a table, and every ordering
 * question has a test.
 *
 * It also keeps the shell honest. `AppShell` needs to know the active item and
 * `Router` needs to know the screen, and both must agree — asking one function
 * is how they cannot disagree.
 */
import { findNavItem, NAV_ITEMS, type NavItem } from '../nav/navigation';

export type PartyKindFilter = 'CUSTOMER' | 'SUPPLIER' | 'ALL';

export type Route =
  /** The invoice list. */
  | { readonly kind: 'documents' }
  /** One saved invoice, for preview and print. */
  | { readonly kind: 'document'; readonly documentId: string }
  /** The invoice editor. */
  | { readonly kind: 'new-invoice' }
  /**
   * An existing DRAFT invoice, in the editor.
   *
   * A separate route from `document`, not a flag on it. The two screens have
   * nothing in common except the lines they show: one is a read-only render with
   * print actions, the other is a form. A single screen with an `editing` prop
   * would have to branch on that prop in every header, button and guard, and the
   * next person to add a button would forget one of the branches.
   */
  | { readonly kind: 'edit-invoice'; readonly documentId: string }
  | { readonly kind: 'quotations' }
  | { readonly kind: 'new-quotation' }
  | { readonly kind: 'edit-quotation'; readonly documentId: string }
  | { readonly kind: 'quotation'; readonly documentId: string }
  | { readonly kind: 'sales-orders' }
  | { readonly kind: 'new-sales-order' }
  | { readonly kind: 'edit-sales-order'; readonly documentId: string }
  | { readonly kind: 'sales-order'; readonly documentId: string }
  /**
   * Purchase Orders.
   *
   * Separate kinds, not a `side` parameter on the sales-order kinds. Purchase
   * Orders are supplier-facing and share no screen with a Sales Order, and the
   * one thing that could go wrong here — a purchase order opening in a sales
   * editor and raising an order against a customer — is prevented by distinct
   * components rather than by a branch someone could get wrong.
   */
  | { readonly kind: 'purchase-orders' }
  | { readonly kind: 'new-purchase-order' }
  | { readonly kind: 'edit-purchase-order'; readonly documentId: string }
  | { readonly kind: 'purchase-order'; readonly documentId: string }
  | { readonly kind: 'delivery-notes' }
  | { readonly kind: 'new-delivery-note'; readonly salesOrderId: string }
  | { readonly kind: 'edit-delivery-note'; readonly documentId: string }
  | { readonly kind: 'delivery-note'; readonly documentId: string }
  /**
   * Credit Notes and Sales Returns.
   *
   * `docType` is carried on the route rather than inferred from the URL, because
   * the two share every screen and differ only in the document they create. Letting
   * each screen decide from its own base would be a second source of truth about
   * which type a document is, and the one place that must not be wrong is the
   * stock rule.
   */
  | { readonly kind: 'credit-notes' }
  | { readonly kind: 'sales-returns' }
  /** Create a draft return from an invoice, then jump to its editor. */
  | { readonly kind: 'new-return'; readonly docType: 'CREDIT_NOTE' | 'SALES_RETURN'; readonly invoiceId: string }
  | { readonly kind: 'edit-credit-note'; readonly documentId: string }
  | { readonly kind: 'credit-note'; readonly documentId: string }
  | { readonly kind: 'edit-sales-return'; readonly documentId: string }
  | { readonly kind: 'sales-return'; readonly documentId: string }
  /** Contacts, filtered to one side. */
  | { readonly kind: 'contacts'; readonly partyKind: PartyKindFilter }
  /** Customer receipts and invoice allocation history. */
  | { readonly kind: 'receipts' }
  /** The item catalogue. */
  | { readonly kind: 'products' }
  /**
   * Backup & Restore.
   *
   * A release blocker: a backup a user cannot open is not a backup.
   */
  | { readonly kind: 'backup' }
/**
 * Cloud sync setup, status, devices, and conflicts.
 *
 * Reached from Settings, and — when the device holds no business at all —
 * from the empty-business gate, which offers a synced-business restore
 * alongside creating a new company.
 */
  | { readonly kind: 'sync' }
  /** Settings → Business Profile. Also the first-run "create your company". */
  | { readonly kind: 'business-profile' }
  /** Help center — bundled documentation, no network required. */
  | { readonly kind: 'help' }
  /** A destination listed in the menu that has no screen yet. */
  | { readonly kind: 'planned'; readonly navItem: NavItem }
  /** A hash that is in no menu at all — a typo, or a stale bookmark. */
  | { readonly kind: 'marketing' }
  | { readonly kind: 'unknown'; readonly route: string };

/**
 * `new` is the editor's path segment, and a document id is a ULID.
 *
 * Checking for the literal word is what keeps `#/documents/new` out of the
 * document branch. The word is matched exactly, so a real id that merely
 * *contains* "new" is still a document.
 */
const NEW_SEGMENT = 'new';

/** `#/sales/invoices`, `#/documents` — both are "the invoice list". */
const INVOICE_LIST = /^#\/(?:sales\/invoices|documents)\/?$/;
const QUOTATION_LIST = /^#\/sales\/quotations\/?$/;
const QUOTATION_EDIT = /^#\/sales\/quotations\/(.+)\/edit\/?$/;
const QUOTATION_NEW = /^#\/sales\/quotations\/new\/?$/;
const QUOTATION_ONE = /^#\/sales\/quotations\/(.+)\/?$/;
const SALES_ORDER_LIST = /^#\/sales\/orders\/?$/;
const SALES_ORDER_NEW = /^#\/sales\/orders\/new\/?$/;
const SALES_ORDER_EDIT = /^#\/sales\/orders\/(.+)\/edit\/?$/;
const SALES_ORDER_ONE = /^#\/sales\/orders\/(.+)\/?$/;
/**
 * The purchase-order routes.
 *
 * `(.+)` AND NOT `([^/]+)`, and that is deliberate — the same reason the delivery-note
 * routes use it. A migrated document id is derived from its legacy PO number, so a
 * real one looks like `biz-1~vendpo-PO-001`, which contains slashes and tildes. A
 * pattern that forbade them would make every migrated purchase order unreachable,
 * while the list still showed the row. The user would click a visible order and be
 * told it did not exist.
 */
const PURCHASE_ORDER_LIST = /^#\/purchases\/orders\/?$/;
const PURCHASE_ORDER_NEW = /^#\/purchases\/orders\/new\/?$/;
const PURCHASE_ORDER_EDIT = /^#\/purchases\/orders\/(.+)\/edit\/?$/;
const PURCHASE_ORDER_ONE = /^#\/purchases\/orders\/(.+)\/?$/;
/**
 * The delivery-note routes.
 *
 * `(.+)` AND NOT `([^/]+)`, and that is deliberate.
 *
 * A migrated document id is derived from its legacy number, so a real one looks
 * like `legacy:1.6.x:company_quotes:business:cpo-PO/26-27/0001` — it CONTAINS a
 * slash. `([^/]+)` stops at the first one, the route fails to match, and the hash
 * falls through to `findNavItem`, which renders the honest "not built yet" state
 * for a milestone that is in fact built. Every other document route here already
 * uses `(.+)`; these three were the only ones that did not, so an imported
 * delivery note was unreachable by link while a freshly created one worked.
 *
 * Ordering is what keeps the greedy match safe: `from-order/<id>` and `<id>/edit`
 * are tested BEFORE the bare `<id>` pattern, so the more specific routes win and
 * `new` is still excluded by name.
 */
const DELIVERY_NOTE_FROM_ORDER = /^#\/sales\/deliveries\/from-order\/(.+)\/?$/;
const DELIVERY_NOTE_EDIT = /^#\/sales\/deliveries\/(.+)\/edit\/?$/;
const DELIVERY_NOTE_LIST = /^#\/sales\/deliveries\/?$/;
const DELIVERY_NOTE_ONE = /^#\/sales\/deliveries\/(.+)\/?$/;

/**
 * Credit Note and Sales Return routes.
 *
 * The EDIT forms are tested before the bare `<id>` forms for the same reason the
 * delivery routes are: with a greedy `(.+)` capture, a bare pattern tested first
 * would swallow the `/edit` suffix into the id and the editor would be
 * unreachable.
 */
const CREDIT_NOTE_FROM_INVOICE = /^#\/sales\/credit-notes\/from-invoice\/(.+)\/?$/;
const CREDIT_NOTE_EDIT = /^#\/sales\/credit-notes\/(.+)\/edit\/?$/;
const CREDIT_NOTE_LIST = /^#\/sales\/credit-notes\/?$/;
const CREDIT_NOTE_ONE = /^#\/sales\/credit-notes\/(.+)\/?$/;
const SALES_RETURN_EDIT = /^#\/sales\/returns\/(.+)\/edit\/?$/;
const SALES_RETURN_FROM_INVOICE = /^#\/sales\/returns\/from-invoice\/(.+)\/?$/;
const SALES_RETURN_LIST = /^#\/sales\/returns\/?$/;
const SALES_RETURN_ONE = /^#\/sales\/returns\/(.+)\/?$/;

/**
 * `#/sales/invoices/<id>/edit` — the DRAFT editor.
 *
 * Three aliases, not two. The app already accepts `#/sales/invoices` and
 * `#/documents` for the same thing, and the editor's own links are written as
 * the shorter `#/invoices/...`. Omitting that third alias is not a cosmetic gap:
 * every "Edit draft" click and every post-save redirect would land on the
 * "No such page" screen, and a working feature would read as broken. The route
 * tests are what caught it.
 *
 * MUST be tested before `INVOICE_ONE`. That pattern is `(.+)$` and is greedy,
 * so `#/invoices/abc/edit` would match it with `documentId === 'abc/edit'` -
 * a document that does not exist, with an error message about a missing
 * invoice instead of opening the editor. Order is the whole mechanism here,
 * exactly as it already is for `/new`.
 */
const INVOICE_EDIT = /^#\/(?:sales\/invoices|documents|invoices)\/([^/]+)\/edit\/?$/;

/** `#/sales/invoices/<id>` or `#/documents/<id>`, but not `/new`. */
const INVOICE_ONE = /^#\/(?:sales\/invoices|documents)\/(.+)$/;

const INVOICE_NEW = /^#\/(?:sales\/invoices|documents)\/new\/?$/;

/** `#/contacts`, `#/contacts/customers`, `#/contacts/suppliers`. */
const CONTACTS = /^#\/contacts(?:\/(customers|suppliers))?\/?$/;
const RECEIPTS = /^#\/payments\/receipts\/?$/;

const PRODUCTS = /^#\/(?:inventory\/products|items)\/?$/;

/**
 * Backup & Restore.
 *
 * The alias `#/backup` is registered because the planned menu entry already sent
 * people there, and a URL we published should not 404 the day the feature lands.
 */
const BACKUP = /^#\/(?:settings\/backup|backup)\/?$/;

/**
 * Business Profile.
 *
 * The bare `#/business` alias is registered for the same reason `#/backup` is:
 * a URL the app has already published should not 404 the day the screen lands.
 */
const BUSINESS_PROFILE = /^#\/(?:settings\/business|business)\/?$/;

/** Help Center — bundled documentation, no network required. */
const HELP = /^#\/help\/?$/;

/**
 * No hash at all, or a bare `#/`.
 *
 * The empty string is included deliberately. The app is opened with NO hash on
 * a first visit, and `window.location.hash` is `''` in that case — so without
 * this the very first screen a user ever sees would be the "unknown route"
 * state. The first screen of the app must be a real one.
 */
const EMPTY = /^(?:#\/?)?$/;

/**
 * What a hash means.
 *
 * The editor is matched BEFORE the one-document pattern, always. That ordering
 * is the whole reason this function exists rather than a chain of `startsWith`
 * calls in JSX.
 */
export function matchRoute(rawHash: string): Route {
  const route = (rawHash || '').trim();

  if (EMPTY.test(route)) return { kind: 'marketing' };
  if (SALES_ORDER_NEW.test(route)) return { kind: 'new-sales-order' };
  const salesOrderEdit = SALES_ORDER_EDIT.exec(route);
  if (salesOrderEdit?.[1]) return { kind: 'edit-sales-order', documentId: salesOrderEdit[1] };
  if (SALES_ORDER_LIST.test(route)) return { kind: 'sales-orders' };
  const salesOrder = SALES_ORDER_ONE.exec(route);
  if (salesOrder?.[1] && salesOrder[1] !== NEW_SEGMENT) return { kind: 'sales-order', documentId: salesOrder[1] };
  // Purchase orders, matched AFTER the sales ones and in their own namespace. The
  // prefixes do not overlap (`#/sales/…` vs `#/purchases/…`), so order is not
  // load-bearing here — but `new` is checked before the generic `(.+)` capture for
  // the same reason as sales orders: `#/purchases/orders/new` would otherwise match
  // `PURCHASE_ORDER_ONE` and render a document screen looking for an order whose id
  // is the word "new".
  if (PURCHASE_ORDER_LIST.test(route)) return { kind: 'purchase-orders' };
  if (PURCHASE_ORDER_NEW.test(route)) return { kind: 'new-purchase-order' };
  const purchaseOrderEdit = PURCHASE_ORDER_EDIT.exec(route);
  if (purchaseOrderEdit?.[1] && purchaseOrderEdit[1] !== NEW_SEGMENT) {
    return { kind: 'edit-purchase-order', documentId: purchaseOrderEdit[1] };
  }
  const purchaseOrder = PURCHASE_ORDER_ONE.exec(route);
  if (purchaseOrder?.[1] && purchaseOrder[1] !== NEW_SEGMENT) {
    return { kind: 'purchase-order', documentId: purchaseOrder[1] };
  }
  const deliveryFromOrder = DELIVERY_NOTE_FROM_ORDER.exec(route);
  if (deliveryFromOrder?.[1]) return { kind: 'new-delivery-note', salesOrderId: deliveryFromOrder[1] };
  const deliveryEdit = DELIVERY_NOTE_EDIT.exec(route);
  if (deliveryEdit?.[1]) return { kind: 'edit-delivery-note', documentId: deliveryEdit[1] };
  if (DELIVERY_NOTE_LIST.test(route)) return { kind: 'delivery-notes' };
  const deliveryNote = DELIVERY_NOTE_ONE.exec(route);
  if (deliveryNote?.[1] && deliveryNote[1] !== NEW_SEGMENT) return { kind: 'delivery-note', documentId: deliveryNote[1] };
  // Credit notes and sales returns, next to the delivery routes they follow from.
  // `from-invoice` is matched FIRST: with greedy `(.+)` captures, the bare `<id>`
  // form would otherwise read "from-invoice/<id>" as a document id.
  const creditNoteFromInvoice = CREDIT_NOTE_FROM_INVOICE.exec(route);
  if (creditNoteFromInvoice?.[1]) {
    return { kind: 'new-return', docType: 'CREDIT_NOTE', invoiceId: creditNoteFromInvoice[1] };
  }
  const creditNoteEdit = CREDIT_NOTE_EDIT.exec(route);
  if (creditNoteEdit?.[1]) return { kind: 'edit-credit-note', documentId: creditNoteEdit[1] };
  if (CREDIT_NOTE_LIST.test(route)) return { kind: 'credit-notes' };
  const creditNote = CREDIT_NOTE_ONE.exec(route);
  if (creditNote?.[1] && creditNote[1] !== NEW_SEGMENT) {
    return { kind: 'credit-note', documentId: creditNote[1] };
  }
  const salesReturnFromInvoice = SALES_RETURN_FROM_INVOICE.exec(route);
  if (salesReturnFromInvoice?.[1]) {
    return { kind: 'new-return', docType: 'SALES_RETURN', invoiceId: salesReturnFromInvoice[1] };
  }
  const salesReturnEdit = SALES_RETURN_EDIT.exec(route);
  if (salesReturnEdit?.[1]) return { kind: 'edit-sales-return', documentId: salesReturnEdit[1] };
  if (SALES_RETURN_LIST.test(route)) return { kind: 'sales-returns' };
  const salesReturn = SALES_RETURN_ONE.exec(route);
  if (salesReturn?.[1] && salesReturn[1] !== NEW_SEGMENT) {
    return { kind: 'sales-return', documentId: salesReturn[1] };
  }
  if (QUOTATION_NEW.test(route)) return { kind: 'new-quotation' };
  const quotationEdit = QUOTATION_EDIT.exec(route);
  if (quotationEdit?.[1]) return { kind: 'edit-quotation', documentId: quotationEdit[1] };
  if (QUOTATION_LIST.test(route)) return { kind: 'quotations' };
  const quotation = QUOTATION_ONE.exec(route);
  if (quotation?.[1] && quotation[1] !== NEW_SEGMENT) return { kind: 'quotation', documentId: quotation[1] };
  if (INVOICE_NEW.test(route)) return { kind: 'new-invoice' };

  // Before `INVOICE_ONE`, which would otherwise swallow the `/edit` suffix.
  const edit = INVOICE_EDIT.exec(route);
  if (edit?.[1] && edit[1] !== NEW_SEGMENT) {
    return { kind: 'edit-invoice', documentId: edit[1] };
  }

  if (RECEIPTS.test(route)) return { kind: 'receipts' };

  const one = INVOICE_ONE.exec(route);
  if (one?.[1] && one[1] !== NEW_SEGMENT) {
    return { kind: 'document', documentId: one[1] };
  }

  if (INVOICE_LIST.test(route)) return { kind: 'documents' };

  const contacts = CONTACTS.exec(route);
  if (contacts) {
    const side = contacts[1];
    return {
      kind: 'contacts',
      partyKind: side === 'suppliers' ? 'SUPPLIER' : side === 'customers' ? 'CUSTOMER' : 'ALL',
    };
  }

  if (PRODUCTS.test(route)) return { kind: 'products' };
  if (BACKUP.test(route)) return { kind: 'backup' };
  if (/^#\/settings\/sync\/?$/.test(route)) return { kind: 'sync' };
  if (BUSINESS_PROFILE.test(route)) return { kind: 'business-profile' };
  if (HELP.test(route)) return { kind: 'help' };

  // A menu destination with nothing behind it yet. Answering from the menu
  // itself means adding a route later cannot leave it showing "not found".
  const navItem = findNavItem(route);
  if (navItem) return { kind: 'planned', navItem };

  return { kind: 'unknown', route };
}

/** Every route that names a real screen. Used by tests to assert coverage. */
export const BUILT_ROUTES: readonly string[] = NAV_ITEMS.filter((i) => i.built).map((i) => i.href);
